'use client';

import { Provider } from 'react-redux';
import { store } from './store';
import { useEffect } from 'react';
import { initializeTheme } from './slices/themeSlice';
import { initializeLanguage } from './slices/languageSlice';
import { setFeatures } from './slices/featuresSlice';
import { setUser, setLoading, logout, setCompany } from './slices/authSlice';
import { api } from '@/lib/api';
import Cookies from 'js-cookie';

function AppInitializer({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    // Initialize theme from localStorage
    store.dispatch(initializeTheme());
    
    // Initialize language from localStorage
    store.dispatch(initializeLanguage());
    
    // Check for existing session and VALIDATE with server
    const validateSession = async () => {
      const sessionData = Cookies.get('session');
      if (sessionData) {
        try {
          // First, parse the cookie
          const user = JSON.parse(sessionData);
          
          // Then validate with server by calling a simple API
          // If server is down or session invalid, this will fail
          await api.healthCheck();
          
          // Server is up and responding, set user
          store.dispatch(setUser(user));
        } catch (error) {
          // Server is down or session invalid - clear everything
          console.error('Session validation failed:', error);
          Cookies.remove('session', { path: '/' });
          Cookies.remove('session_token', { path: '/' });
          store.dispatch(logout());
          store.dispatch(setLoading(false));
        }
      } else {
        store.dispatch(setLoading(false));
      }
    };
    
    validateSession();
    
    // Load feature toggles
    const loadFeatures = async () => {
      try {
        const features = await api.getFeatures();
        store.dispatch(setFeatures(features));
      } catch (error) {
        // Silently fail - features will use defaults
        console.error('Failed to load features:', error);
      }
    };
    
    loadFeatures();
    
    // Load company data
    const loadCompany = async () => {
      try {
        const company = await api.getCompany();
        store.dispatch(setCompany(company));
      } catch (error) {
        // Silently fail - company will be null
        console.error('Failed to load company:', error);
      }
    };
    
    loadCompany();
  }, []);

  return <>{children}</>;
}

export function ReduxProvider({ children }: { children: React.ReactNode }) {
  return (
    <Provider store={store}>
      <AppInitializer>{children}</AppInitializer>
    </Provider>
  );
}

